import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import Buttons from "../../Components/Buttons/Buttons";
import FormInputs from "../../Components/FormInputs/FormInputs";
import Loader from "../../Components/Loader/Loader";
import { ADD_NEW_LEAD_STUDENT, EDIT_LEAD_STUDENT } from "../../Utlis/apiPath";
import { postApi, putApi } from "../../Utlis/apiService";
import { errorToast, successToast } from "../../Services/ToastHelper";
import { BackArrowIcon } from "../../Components/Icons/BackArrowIcon";
import "./AddStudentPage.scss";
import RollNumberConfigModal from "./Rollnumberconfigmodal";
import { SettingOutlined } from "@ant-design/icons";

const AddStudentPage = ({ editMode = false, initialData = null, onUpdate }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const routeState = location?.state || {};
  const routeEditMode = editMode || routeState.editMode;
  const routeInitialData = initialData || routeState.initialData || null;

  const [leadStudentData, setLeadStudentData] = useState({});
  const [leadStudentErrors, setLeadStudentErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isRollNumberConfigVisible, setIsRollNumberConfigVisible] =
    useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");

  useEffect(() => {
    if (routeEditMode && routeInitialData) {
      setLeadStudentData({
        fullName: routeInitialData.firstName || "",
        lastName: routeInitialData.lastName || "",
        email: routeInitialData.email || "",
        contactNumber: routeInitialData.phoneNumber || "",
        country: routeInitialData.countryName || "",
        city: routeInitialData.city || "",
      });
      setPhoneNumber(routeInitialData.phoneNumber || "");
    } else if (editMode && initialData) {
      setLeadStudentData({
        fullName: initialData.firstName || "",
        lastName: initialData.lastName || "",
        email: initialData.email || "",
        contactNumber: initialData.phoneNumber || "",
        country: initialData.countryName || "",
        city: initialData.city || "",
      });
      setPhoneNumber(initialData.phoneNumber || "");
    } else {
      setLeadStudentData({});
      setPhoneNumber("");
    }
  }, [routeEditMode, routeInitialData, editMode, initialData]);

  const validateLeadStudentData = () => {
    let objErrors = {};
    if (!leadStudentData.fullName || leadStudentData.fullName.trim() === "") {
      objErrors.fullName = "Full Name is required";
    }
    if (!leadStudentData.lastName || leadStudentData.lastName.trim() === "") {
      objErrors.lastName = "Last Name is required";
    }
    if (!leadStudentData.email || leadStudentData.email.trim() === "") {
      objErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(leadStudentData.email)) {
      objErrors.email = "Please enter a valid email address";
    }
    const phoneDigits = (phoneNumber || "").replace(/\D/g, "");
    if (!phoneDigits) {
      objErrors.contactNumber = "Contact Number is required";
    } else if (phoneDigits.length < 10) {
      objErrors.contactNumber = "Please enter a valid contact number";
    }
    if (!leadStudentData.country || leadStudentData.country.trim() === "") {
      objErrors.country = "Country is required";
    }
    if (!leadStudentData.city || leadStudentData.city.trim() === "") {
      objErrors.city = "City is required";
    }
    return objErrors;
  };

  const handleLeadStudent = async () => {
    const errors = validateLeadStudentData();
    if (Object.keys(errors).length > 0) {
      setLeadStudentErrors(errors);
      return;
    }

    if ((routeEditMode || editMode) && onUpdate) {
      // Extract country code and clean phone number
      const fullPhone = phoneNumber.replace(/\D/g, ''); // Remove all non-digits
      const countryCode = fullPhone.substring(0, fullPhone.length - 10); // Everything except last 10 digits
      const cleanPhoneNumber = fullPhone.slice(-10); // Last 10 digits
      
      const updatedData = {
        studentId:
          (routeInitialData && routeInitialData.studentId) ||
          (initialData && initialData.studentId),
        firstName: leadStudentData.fullName,
        lastName: leadStudentData.lastName,
        email: leadStudentData.email,
        phoneNumber: cleanPhoneNumber,
        countryCode: countryCode,
        countryName: leadStudentData.country,
        city: leadStudentData.city,
      };
      await onUpdate(updatedData);
      navigate("/lead_student", { state: { studentAdded: true } });
      return;
    }

    // If edit mode was triggered via route state (no onUpdate passed), perform the update here
    if (routeEditMode && !onUpdate) {
      // Extract country code and clean phone number
      const fullPhone = phoneNumber.replace(/\D/g, ''); // Remove all non-digits
      const countryCode = fullPhone.substring(0, fullPhone.length - 10); // Everything except last 10 digits
      const cleanPhoneNumber = fullPhone.slice(-10); // Last 10 digits
      
      const updatedData = {
        studentId: routeInitialData && routeInitialData.studentId,
        firstName: leadStudentData.fullName,
        lastName: leadStudentData.lastName,
        email: leadStudentData.email,
        phoneNumber: cleanPhoneNumber,
        countryCode: countryCode,
        countryName: leadStudentData.country,
        city: leadStudentData.city,
      };

      try {
        setIsLoading(true);
        const response = await putApi(EDIT_LEAD_STUDENT, updatedData);
        if (response.status === 200) {
          successToast(response.message || "Student updated successfully");
          navigate("/lead_student", { state: { studentAdded: true } });
        } else {
          errorToast(response.message || "Failed to update student");
        }
      } catch (error) {
        console.error("Error updating student:", error);
        errorToast(
          error?.response?.data?.message ||
            error?.message ||
            "An error occurred while updating student"
        );
      } finally {
        setIsLoading(false);
      }

      return;
    }

    try {
      setIsLoading(true);
      // Extract country code and clean phone number
      const fullPhone = phoneNumber.replace(/\D/g, ''); // Remove all non-digits
      const countryCode = fullPhone.substring(0, fullPhone.length - 10); // Everything except last 10 digits
      const cleanPhoneNumber = fullPhone.slice(-10); // Last 10 digits
      
      const payload = {
        firstName: leadStudentData.fullName,
        lastName: leadStudentData.lastName,
        email: leadStudentData.email,
        phoneNumber: cleanPhoneNumber,
        countryCode: countryCode,
        countryName: leadStudentData.country,
        city: leadStudentData.city,
      };
      const response = await postApi(ADD_NEW_LEAD_STUDENT, payload);

      if (response.status === 200) {
        successToast(response.message || "Student added successfully");
        navigate("/lead_student", { state: { studentAdded: true } });
      } else {
        errorToast(response.message || "Failed to add student");
      }
    } catch (error) {
      console.error("Error adding lead student:", error);
      errorToast(
        error?.response?.data?.message ||
          error?.message ||
          "An error occurred while adding student"
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="add-student-page">
      <div className="page-header">
        <div
          style={{
            cursor: "pointer",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 10,
          }}
          onClick={() => navigate(-1)}
        >
          <BackArrowIcon />
          <h2 style={{ margin: 0 }}>
            {routeEditMode || editMode ? "Edit Student" : "Add New Student"}
          </h2>
        </div>

        <div
          style={{ display: "flex", justifyContent: "right", marginTop: 20 }}
        >
          <Buttons
            variant="primary"
            onClick={() => setIsRollNumberConfigVisible(true)}
          >
            <SettingOutlined /> Roll Number Config
          </Buttons>

          <Buttons variant="primary" onClick={handleLeadStudent}>
            {routeEditMode || editMode ? "Update" : "Save"}
          </Buttons>
        </div>
      </div>

      {isLoading && (
        <div className="loading-overlay">
          <Loader />
        </div>
      )}

      {/* General Details Section */}
      <div className="form-section">
        <h3 className="section-heading">General Details</h3>
        <div className="add-student-form-grid">
          <div className="form-col">
            <FormInputs
              title="First Name"
              type="text"
              placeholder="Enter first name"
              value={leadStudentData.fullName || ""}
              onChange={(e) => {
                setLeadStudentData({
                  ...leadStudentData,
                  fullName: e.target.value,
                });
                setLeadStudentErrors({ ...leadStudentErrors, fullName: "" });
              }}
              errorMessage={leadStudentErrors.fullName}
            />
          </div>

          <div className="form-col">
            <FormInputs
              title="Last Name"
              type="text"
              placeholder="Enter last name"
              value={leadStudentData.lastName || ""}
              onChange={(e) => {
                setLeadStudentData({
                  ...leadStudentData,
                  lastName: e.target.value,
                });
                setLeadStudentErrors({ ...leadStudentErrors, lastName: "" });
              }}
              errorMessage={leadStudentErrors.lastName}
            />
          </div>
        </div>
      </div>

      {/* Contact Details Section */}
      <div className="form-section">
        <h3 className="section-heading">Contact Details</h3>
        <div className="add-student-form-grid">
          <div className="form-col">
            <FormInputs
              title="Email"
              type="email"
              placeholder="Enter email"
              value={leadStudentData.email || ""}
              onChange={(e) => {
                setLeadStudentData({
                  ...leadStudentData,
                  email: e.target.value,
                });
                setLeadStudentErrors({ ...leadStudentErrors, email: "" });
              }}
              errorMessage={leadStudentErrors.email}
            />
          </div>

          <div className="form-col">
            <label>Contact Number</label>
            <PhoneInput
              country={"in"}
              value={phoneNumber}
              onChange={(phone) => {
                setPhoneNumber(phone);
                setLeadStudentErrors({ ...leadStudentErrors, contactNumber: "" });
              }}
              inputProps={{
                name: "phone",
                required: true,
                autoFocus: false,
              }}
              enableAreaCodes={true}
              disableDropdown={false}
              containerStyle={{ width: "100%" }}
              inputStyle={{ 
                width: "100%", 
                height: "38px", 
                fontSize: "14px",
                paddingLeft: "50px"
              }}
              dropdownStyle={{ paddingLeft: "10px" }}
            />
            {leadStudentErrors.contactNumber && (
              <p style={{ color: "red", fontSize: "12px", marginTop: "5px" }}>
                {leadStudentErrors.contactNumber}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Address Details Section */}
      <div className="form-section">
        <h3 className="section-heading">Address Details</h3>
        <div className="add-student-form-grid">
          <div className="form-col">
            <FormInputs
              title="Country"
              type="text"
              name="country"
              placeholder="Enter country"
              value={leadStudentData.country || ""}
              onChange={(e) => {
                setLeadStudentData({
                  ...leadStudentData,
                  country: e.target.value,
                });
                setLeadStudentErrors({ ...leadStudentErrors, country: "" });
              }}
              autoComplete="country-name"
              errorMessage={leadStudentErrors.country}
            />
          </div>

          <div className="form-col">
            <FormInputs
              title="City"
              type="text"
              placeholder="Enter city"
              value={leadStudentData.city || ""}
              name="city"
              onChange={(e) => {
                setLeadStudentData({
                  ...leadStudentData,
                  city: e.target.value,
                });
                setLeadStudentErrors({ ...leadStudentErrors, city: "" });
              }}
              errorMessage={leadStudentErrors.city}
              autoComplete="address-level2"
            />
          </div>
        </div>
      </div>
      {/* Roll Number Configuration Modal */}
      <RollNumberConfigModal
        isVisible={isRollNumberConfigVisible}
        onClose={() => setIsRollNumberConfigVisible(false)}
      />
    </div>
  );
};

export default AddStudentPage;
